import React, { useRef, useState } from "react";
import { MESSAGE_MODALS } from "../../context/custom/MessageModalContext";
import { ALERT_TYPE, REQUEST_METHOD, ShowToastMessage } from '@laramie/ui';
import { APIS } from "../../config/apis";
import { ServerRequest } from "../../utils/apiweb";
import {
    TableCustom,
    SectionHeading,
    WrapperAdvancedSearch,
} from "../../components/common";
import { useLista, useManagedContext } from "../../components/hooks";
import VariableModal from "../../components/controls/VariableModal";
import { PERMISSIONS } from "../../consts/permissions";
import { usePermissions } from "../../components/hooks/usePermissions";

function VariablesView() {
    const { setIsLoading, showMessageModal } = useManagedContext();

    const [state, setState] = useState({
        showForm: false,
        modeFormEdit: false,
        rowId: 0,
        listAll: [],
        list: [],
    });
    const [tableVisible, setTableVisible] = useState(false);

    const searchRef = useRef();

    const { hasPermission } = usePermissions();
    const canEdit = hasPermission(PERMISSIONS.VARIABLE_EDIT);

    const [, getRowLista] = useLista({
        listas: ["TipoTributo"],
        onLoaded: (listas, isSuccess, error) => {
            if (isSuccess) {
                SearchVariables();
            } else {
                ShowToastMessage(ALERT_TYPE.ALERT_ERROR, error);
            }
        },
        memo: {
            key: "TipoTributo",
            timeout: 0,
        },
    });

    const getDescTipoTributo = (id) => {
        const row = getRowLista("TipoTributo", id);
        return row ? row.nombre : "Todos los tributos";
    };

    const tableColumns = [
        {
            Header: "Tipo de tributo",
            Cell: (props) => {
                const row = props.row.original;
                const tipoTributo = getDescTipoTributo(row.idTipoTributo);
                return tipoTributo;
            },
            id: "tipoTributo",
            accessor: "id",
            width: "10%",
        },
        { Header: "Código", accessor: "codigo", width: "27%" },
        { Header: "Descripción", accessor: "descripcion", width: "31%" },
        {
            Header: "Global",
            Cell: (props) => (props.row.original.global ? "Sí" : "No"),
            id: "global",
            accessor: "id",
            width: "8%",
        },
        {
            Header: "Predef.",
            Cell: (props) => (props.row.original.predefinido ? "Sí" : "No"),
            id: "predefinido",
            accessor: "id",
            width: "8%",
        },
        {
            Header: "Activo",
            Cell: (props) => (props.row.original.activo ? "Sí" : "No"),
            id: "activo",
            accessor: "id",
            width: "8%",
        },
    ];

    const avmr = {
        onAdd: () => {
            setState((prevState) => ({
                ...prevState,
                showForm: true,
                modeFormEdit: true,
                rowId: 0,
            }));
        },
        onView: ({ id }) => {
            setState((prevState) => ({
                ...prevState,
                showForm: true,
                modeFormEdit: false,
                rowId: parseInt(id),
            }));
        },
        onModify: ({ id }) => {
            setState((prevState) => ({
                ...prevState,
                showForm: true,
                modeFormEdit: true,
                rowId: parseInt(id),
            }));
        },
        onRemove: ({ id }) => {
            const variable = state.list.find((x) => x.id === id);
            showMessageModal({
                ...MESSAGE_MODALS.BORRAR,
                message: `¿Está seguro de borrar la variable ${variable.codigo}?`,
                onConfirm: () => {
                    RemoveVariable(id);
                },
            });
        },
    };

    function SearchVariables() {
        setIsLoading(true);
        setState((prevState) => ({ ...prevState, listAll: [], list: [] }));

        const callbackSuccess = (response) => {
            response
                .json()
                .then((data) => {
                    setIsLoading(false);
                    setState((prevState) => ({
                        ...prevState,
                        listAll: data,
                        list: FilterVariables(data),
                    }));
                })
                .catch((error) => {
                    ShowToastMessage(
                        ALERT_TYPE.ALERT_ERROR,
                        "Error procesando respuesta: " + error
                    );
                    setIsLoading(false);
                });
        };

        ServerRequest(
            REQUEST_METHOD.GET,
            null,
            true,
            APIS.URLS.VARIABLE,
            null,
            null,
            callbackSuccess,
            (response) => {
                response.json().then((error) => {
                    ShowToastMessage(ALERT_TYPE.ALERT_ERROR, error.message);
                    setIsLoading(false);
                });
            },
            (error) => {
                ShowToastMessage(
                    ALERT_TYPE.ALERT_ERROR,
                    "Error procesando solicitud: " + error.message
                );
                setIsLoading(false);
            }
        );
    }

    function FilterVariables(data) {
        const {
            idTipoTributo,
            codigo,
            activo,
        } = searchRef.current?.filters || {};

        let newData = [...data];

        if (idTipoTributo && parseInt(idTipoTributo, 10) > 0) {
            newData = newData.filter(
                (f) => f.idTipoTributo === null || f.idTipoTributo === parseInt(idTipoTributo, 10)
            );
        }
        if (codigo && codigo.length > 0) {
            newData = newData.filter(
                (f) => `${f.codigo}|${f.descripcion}`.toLowerCase().indexOf(codigo.toLowerCase()) >= 0
            );
        }
        if (activo) {
            newData = newData.filter((f) => f.activo);
        }

        return newData;
    }

    function RemoveVariable(id) {
        setIsLoading(true);

        const callbackSuccess = (response) => {
            response
                .json()
                .then(() => {
                    setIsLoading(false);
                    SearchVariables();
                })
                .catch((error) => {
                    ShowToastMessage(
                        ALERT_TYPE.ALERT_ERROR,
                        "Error procesando respuesta: " + error
                    );
                    setIsLoading(false);
                });
        };

        const paramsUrl = `/${id}`;

        ServerRequest(
            REQUEST_METHOD.DELETE,
            null,
            true,
            APIS.URLS.VARIABLE,
            paramsUrl,
            null,
            callbackSuccess,
            (response) => {
                response.json().then((error) => {
                    ShowToastMessage(ALERT_TYPE.ALERT_ERROR, error.message);
                    setIsLoading(false);
                });
            },
            (error) => {
                ShowToastMessage(
                    ALERT_TYPE.ALERT_ERROR,
                    "Error procesando solicitud: " + error.message
                );
                setIsLoading(false);
            }
        );
    }

    function onSearch() {
        setState((prev) => ({
            ...prev,
            list: FilterVariables(prev.listAll),
        }));
        setTableVisible(true);
    }

    return (
        <>
            {state.showForm && (
                <VariableModal
                    id={state.rowId}
                    disabled={!state.modeFormEdit}
                    onDismiss={() => {
                        setState((prevState) => ({ ...prevState, showForm: false }));
                    }}
                    onConfirm={() => {
                        setState((prevState) => ({
                            ...prevState,
                            showForm: false,
                            rowId: 0,
                        }));
                        SearchVariables();
                    }}
                />
            )}

            <SectionHeading
                titles={[{ title: "Configuración" }, { title: "Variables" }]}
                onAddClick={canEdit ? avmr.onAdd : null}
                tableVisible={tableVisible}
            />

            <section className="section-accordion">
                <WrapperAdvancedSearch
                    ref={searchRef}
                    filters={[
                        {
                            title: "Tipo de tributo",
                            field: "idTipoTributo",
                            type: "list",
                            lista: "TipoTributo",
                            size: 3,
                            textItemZero: "Todos los tributos"
                        },
                        {
                            title: "Código / Descripción",
                            field: "codigo",
                            type: "string",
                            size: 6,
                            searchOnEnter: true,
                        },
                        {
                            title: "Estado",
                            field: "activo",
                            type: "selectLista",
                            size: 3,
                            options: [
                                { value: null, label: "Todos" },
                                { value: true, label: "Activos" },
                            ],
                        },
                    ]}
                    onSearch={onSearch}
                />

                {tableVisible && (
                    <TableCustom
                        className={"TableCustomBase m-top-20"}
                        columns={tableColumns}
                        data={state.list}
                        avmr={avmr}
                        editCode={PERMISSIONS.VARIABLE_EDIT}
                        messageEmpty="No se encontraron resultados"
                    />
                )}
            </section>
        </>
    );
}

export default VariablesView;

